import { useEffect, useRef } from "react";
import { readLevels } from "@/lib/voiceMeter";

const BARS = 14, CELLS = 7;
// Rows from the bottom: red, then grey, then white at the top.
const RED = "239,68,68", GREY = "156,163,175", WHITE = "255,255,255";
const rowColor = (c) => (c < 2 ? RED : c < 5 ? GREY : WHITE);

// Segmented equalizer that follows the AI voice; small resting bars when silent.
export function VoiceBars() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const g = canvas.getContext("2d");
    const levels = new Array(BARS).fill(0);
    let raf;
    const draw = (time) => {
      const w = canvas.clientWidth, h = canvas.clientHeight, dpr = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(w * dpr)) { canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); }
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, w, h);
      const live = readLevels(BARS) || [];
      // Bars use the top part; a faded mirror image ("floor reflection") fills the rest.
      const floor = h * 0.76, REFLECT = 2, pad = w * 0.1, topPad = h * 0.1;
      const gap = w * 0.014, bw = (w - pad * 2 - gap * (BARS - 1)) / BARS, ch = (floor - topPad - gap * CELLS) / CELLS;
      for (let i = 0; i < BARS; i++) {
        const target = Math.max(live[i] || 0, 0.06 + 0.05 * (1 + Math.sin(time / 420 + i * 0.9)));
        levels[i] += (target - levels[i]) * (target > levels[i] ? 0.6 : 0.15);
        const lit = Math.max(1, Math.round(levels[i] * CELLS));
        const resting = (live[i] || 0) < 0.12;
        const x = pad + i * (bw + gap);
        for (let c = 0; c < CELLS; c++) {
          const on = c < lit;
          const col = rowColor(c);
          g.shadowColor = `rgba(${col},0.9)`;
          g.shadowBlur = on ? (resting ? 4 : 10) : 0;
          g.fillStyle = `rgba(${col},${on ? (resting ? 0.45 : 1) : 0.08})`;
          g.fillRect(x, floor - gap - (c + 1) * ch - c * gap, bw, ch);
        }
        g.shadowBlur = 0;
        for (let c = 0; c < Math.min(lit, REFLECT); c++) {
          g.fillStyle = `rgba(${rowColor(c)},${(resting ? 0.12 : 0.3) * (1 - c / REFLECT)})`;
          g.fillRect(x, floor + c * (ch + gap), bw, ch);
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="w-full h-full bg-black flex flex-col" style={{ containerType: "inline-size" }} data-testid="voice-bars">
      <canvas ref={canvasRef} className="w-full flex-1 min-h-0" data-testid="voice-bars-canvas" />
      <div className="shrink-0 px-[5%] pb-[5%] pt-[4%] flex items-center justify-center gap-[0.5em] font-display font-normal text-white leading-tight whitespace-nowrap"
        style={{ fontSize: "6.5cqw" }} data-testid="voice-bars-label">
        <span className="w-[1.1em] h-[1.1em] rounded-full bg-white rec-pulse relative top-[0.2em]" data-testid="voice-bars-rec-dot" /> Reaction
      </div>
    </div>
  );
}
